import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Copy, Search, Plug, Users } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Ajuda para quem usa a API: mostra os IDs que a API pede — o da CONEXAO
// (whatsappId) e o dos GRUPOS (o numero do grupo) — cada um com botao de
// copiar. Antes esses ids nao apareciam em lugar nenhum.

const LinhaId = ({ nome, id, onCopiar }) => (
	<div className="flex items-center gap-2 py-1.5">
		<span className="min-w-0 flex-1 truncate text-sm">{nome}</span>
		<code className="rounded bg-muted px-2 py-0.5 font-mono text-xs">{id}</code>
		<Button
			type="button"
			variant="ghost"
			size="icon"
			className="h-7 w-7 shrink-0"
			aria-label={`Copiar ${id}`}
			title="Copiar ID"
			onClick={() => onCopiar(id)}
		>
			<Copy className="h-3.5 w-3.5" />
		</Button>
	</div>
);

const ApiIds = () => {
	const [conexoes, setConexoes] = useState([]);
	const [grupos, setGrupos] = useState([]);
	const [busca, setBusca] = useState("");
	const [carregandoGrupos, setCarregandoGrupos] = useState(false);

	const copiar = (texto) => {
		try {
			navigator.clipboard.writeText(String(texto));
			toast.success("ID copiado.");
		} catch {
			toast.error("Não consegui copiar. Selecione e copie na mão.");
		}
	};

	useEffect(() => {
		const carregar = async () => {
			try {
				const { data } = await api.get("/whatsapp/");
				setConexoes(data || []);
			} catch (err) {
				toastError(err);
			}
		};
		carregar();
	}, []);

	const carregarGrupos = useCallback(async (termo) => {
		setCarregandoGrupos(true);
		try {
			const { data } = await api.get("/contacts", {
				params: { tipo: "grupos", pageNumber: 1, searchParam: termo || "" },
			});
			setGrupos(data?.contacts || []);
		} catch (err) {
			toastError(err);
		}
		setCarregandoGrupos(false);
	}, []);

	useEffect(() => {
		const t = setTimeout(() => carregarGrupos(busca), 400);
		return () => clearTimeout(t);
	}, [busca, carregarGrupos]);

	return (
		<div className="space-y-5 border-t pt-4">
			{/* Conexões */}
			<div>
				<div className="mb-1 flex items-center gap-1.5 text-sm font-medium">
					<Plug className="h-4 w-4" />
					Suas conexões — use o ID em <span className="font-mono">whatsappId</span>
				</div>
				<div className="divide-y rounded-lg border px-3">
					{conexoes.length === 0 ? (
						<p className="py-3 text-sm text-muted-foreground">
							Nenhuma conexão.
						</p>
					) : (
						conexoes.map((c) => (
							<LinhaId key={c.id} nome={c.name} id={c.id} onCopiar={copiar} />
						))
					)}
				</div>
			</div>

			{/* Grupos */}
			<div>
				<div className="mb-1 flex items-center gap-1.5 text-sm font-medium">
					<Users className="h-4 w-4" />
					Seus grupos — use o ID em <span className="font-mono">number</span>
				</div>
				<div className="relative mb-2">
					<Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={busca}
						onChange={(e) => setBusca(e.target.value)}
						placeholder="Buscar grupo pelo nome…"
						className="pl-8"
					/>
				</div>
				<div className="max-h-64 divide-y overflow-y-auto rounded-lg border px-3">
					{carregandoGrupos ? (
						<p className="py-3 text-sm text-muted-foreground">Carregando…</p>
					) : grupos.length === 0 ? (
						<p className="py-3 text-sm text-muted-foreground">
							Nenhum grupo encontrado.
						</p>
					) : (
						grupos.map((g) => (
							<LinhaId
								key={g.id}
								nome={g.name || g.number}
								id={g.number}
								onCopiar={copiar}
							/>
						))
					)}
				</div>
				<p className="mt-1 text-xs text-muted-foreground">
					O ID do grupo é esse número comprido. Envie para grupo pela conexão
					que está dentro dele.
				</p>
			</div>
		</div>
	);
};

export default ApiIds;
